import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { useSeo } from "@/pages/glossary/PublicShell";
import AuthShell from "@/components/AuthShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck } from "lucide-react";

export default function OpsLogin() {
  const { login, logout } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useSeo({ title: "Ops Console — KidPath", noindex: true });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const user = await login(email, password);
      if (!["admin", "super_admin"].includes(user.role)) {
        await logout();
        setError("This account is not authorized for the Ops Console.");
        return;
      }
      navigate("/ops");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell testid="ops-login-page" title="Ops Console" subtitle="Restricted access. Authorized staff only.">
      <div className="flex items-center gap-2 text-kid-teal text-sm mb-5 font-poppins" data-testid="ops-login-badge">
        <ShieldCheck size={18} /> KidPath internal operations
      </div>
      <form onSubmit={submit} className="space-y-5" data-testid="ops-login-form">
        {error && (
          <div className="text-sm text-kid-coral bg-red-50 border border-red-100 rounded-xl px-4 py-3" data-testid="ops-login-error">
            {error}
          </div>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                 required data-testid="ops-login-email-input" placeholder="you@kidpath.ca" className="h-12 rounded-xl" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                 required data-testid="ops-login-password-input" placeholder="••••••••" className="h-12 rounded-xl" />
        </div>
        <button type="submit" disabled={busy} className="kid-btn kid-btn-primary w-full h-12 text-base disabled:opacity-60" data-testid="ops-login-submit-button">
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </AuthShell>
  );
}
